import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { MessageService } from "primeng/api";
import { AdminFeaturesService } from "../service/admin-features.service";

/**
 * The fixed parts of an ISSO invoice - letterhead, PAN, bank account, number
 * series. Nothing here is per payment; it is what every document carries.
 */
@Component({
  selector: "app-payment-settings",
  templateUrl: "./payment-settings.component.html",
  providers: [MessageService],
})
export class PaymentSettingsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  loading = true;
  saving = false;
  form: any = null;
  preview = "";
  /** The number the series is on when the screen loads, to warn on a change. */
  private loadedNextNumber = 0;

  constructor(private svc: AdminFeaturesService, private toast: MessageService) {}

  ngOnInit() { this.load(); }
  ngOnDestroy() { this.destroy$.next(); this.destroy$.complete(); }

  load() {
    this.loading = true;
    this.svc.billingSettings().pipe(takeUntil(this.destroy$)).subscribe(
      (r: any) => {
        this.form = { org: r.org, bank: r.bank, invoice: r.invoice };
        this.preview = r.preview;
        this.loadedNextNumber = r.invoice.nextNumber;
        this.loading = false;
      },
      () => { this.loading = false; this.fail("Could not load the billing settings."); },
    );
  }

  /** Live preview of the next invoice number, without waiting for a save. */
  get livePreview(): string {
    if (!this.form) return "";
    const i = this.form.invoice;
    const now = new Date();
    const start = now.getMonth() + 1 >= 6 ? now.getFullYear() : now.getFullYear() - 1;
    const fy = String(start).slice(2) + "-" + String(start + 1).slice(2);
    return `${i.prefix} /${fy}/ ${i.seriesAffiliation}${i.nextNumber}`;
  }

  save() {
    if (!this.form) return;
    const n = Number(this.form.invoice.nextNumber);
    if (!Number.isInteger(n) || n < 1) { this.fail("Next invoice number must be a whole number."); return; }
    // Moving the counter backwards can collide with a number already issued.
    // The unique index would refuse it, but a warning here is kinder than a
    // failed download later.
    if (n < this.loadedNextNumber &&
        !confirm(`The series is on ${this.loadedNextNumber}. Setting it back to ${n} can repeat a number already on an issued invoice. Continue?`)) {
      return;
    }

    this.saving = true;
    this.form.invoice.nextNumber = n;
    this.svc.saveBillingSettings(this.form).pipe(takeUntil(this.destroy$)).subscribe(
      (r: any) => {
        this.saving = false;
        this.preview = r.preview;
        this.loadedNextNumber = r.invoice.nextNumber;
        this.toast.add({ key: "custom", severity: "success", summary: "Billing settings saved" });
      },
      (e: any) => {
        this.saving = false;
        this.fail((e && e.error && e.error.error) || "Could not save the settings.");
      },
    );
  }

  private fail(text: string) {
    this.toast.add({ key: "custom", severity: "error", summary: text, life: 7000 });
  }
}
